import React, { useMemo, useState } from "react";
import { Button, Input, Label, Switch, Text, Tooltip } from "@fluentui/react-components";
import { Add20Filled, Delete20Regular, ChevronRight20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import "../CSS/QueueClassification.css";

/**
 * Classification d'appel — construction visuelle de l'arbre des catégories.
 *
 * ─── La forme stockée ───────────────────────────────────────────────────────────────────────
 * `queue.classification = { enabled: bool, value: { "Catégorie": { "Sous-catégorie": {} } } }`
 *
 * Une clef = une catégorie, sa valeur = le niveau suivant, `{}` = feuille. C'est la structure que
 * la console parcourt récursivement ; elle n'est pas de notre invention, on l'édite telle quelle.
 *
 * ─── Pourquoi un éditeur visuel et pas une zone JSON ────────────────────────────────────────
 * Un administrateur métier ne saisit pas du JSON imbriqué sans se tromper, et l'erreur ne se voit
 * pas ici : elle se voit chez l'agent, en ligne avec un client, sous la forme d'un onglet qui ne
 * s'ouvre pas. Chaque geste possible ici produit une structure valide par construction.
 *
 * ─── Le piège des clefs en double ───────────────────────────────────────────────────────────
 * Les catégories sont des CLEFS d'objet : deux sœurs du même nom n'en font qu'une, la seconde
 * écrasant silencieusement la première avec toute sa descendance. C'est la faute la plus facile à
 * commettre et la plus difficile à comprendre après coup — d'où le refus du renommage vers un nom
 * déjà pris entre frères et sœurs, et le message qui l'explique.
 *
 * ─── Profondeur inégale : autorisée, et c'est voulu ─────────────────────────────────────────
 * Une branche à deux niveaux peut cohabiter avec une branche à trois. La console calcule la
 * profondeur MAXIMALE de l'arbre et remplit les niveaux non choisis avec `null` ; le serveur les
 * retire à l'enregistrement. Rien ne casse, et l'écran n'a donc pas à l'interdire.
 */

/** Un nœud et ses enfants. Récursif, comme l'arbre lui-même. */
const TreeNode = ({ path, name, node, onRename, onDelete, onAddChild, readOnly, t }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const [error, setError] = useState(null);
    const childNames = Object.keys(node || {});

    const commit = () => {
        const next = draft.trim();
        if (next === name) { setEditing(false); setError(null); return; }
        const problem = onRename(path, next);
        if (problem) { setError(problem); return; }
        setEditing(false);
        setError(null);
    };

    return (
        <div className="qcl-node">
            <div className="qcl-node-row" style={{ marginLeft: (path.length - 1) * 22 }}>
                {path.length > 1 && <ChevronRight20Regular className="qcl-node-arrow" />}
                {editing ? (
                    <Input
                        size="small"
                        value={draft}
                        autoFocus
                        onChange={(_, d) => { setDraft(d.value); setError(null); }}
                        onBlur={commit}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") commit();
                            if (e.key === "Escape") { setDraft(name); setEditing(false); setError(null); }
                        }}
                    />
                ) : (
                    <span
                        className={`qcl-node-name ${readOnly ? "is-readonly" : ""}`}
                        onClick={() => { if (!readOnly) { setDraft(name); setEditing(true); } }}
                        title={readOnly ? "" : t("CLASSIFICATION_CLICK_TO_RENAME")}
                    >
                        {name}
                    </span>
                )}

                {childNames.length > 0 && (
                    <span className="qcl-node-count">{childNames.length}</span>
                )}

                {!readOnly && (
                    <span className="qcl-node-actions">
                        <Tooltip content={t("CLASSIFICATION_ADD_CHILD")} relationship="label">
                            <Button appearance="subtle" size="small" icon={<Add20Filled />}
                                onClick={() => onAddChild(path)} />
                        </Tooltip>
                        <Tooltip content={t("CLASSIFICATION_DELETE_NODE")} relationship="label">
                            <Button appearance="subtle" size="small" icon={<Delete20Regular />}
                                onClick={() => onDelete(path)} />
                        </Tooltip>
                    </span>
                )}
            </div>

            {error && <div className="qcl-node-error" style={{ marginLeft: (path.length - 1) * 22 }}>{error}</div>}

            {childNames.map((child) => (
                <TreeNode
                    key={child}
                    path={[...path, child]}
                    name={child}
                    node={node[child]}
                    onRename={onRename}
                    onDelete={onDelete}
                    onAddChild={onAddChild}
                    readOnly={readOnly}
                    t={t}
                />
            ))}
        </div>
    );
};

const QueueClassification = (props) => {
    const { t } = useTranslation();
    const readOnly = props.isUpdate ? !props.permissions?.canUpdate : !props.permissions?.canCreate;

    const classification = props.queue?.classification || {};
    // Mémoïsé : sans ça, `{}` est un objet NEUF à chaque rendu, et le `useMemo` de la
    // profondeur plus bas se recalculerait sans cesse.
    const tree = useMemo(() => classification.value || {}, [classification.value]);

    const setClassification = (changes) =>
        props.setQueue((queue) => ({
            ...queue,
            classification: { ...(queue.classification || {}), ...changes },
        }));

    /** Applique `fn` au conteneur désigné par `path`, sur une COPIE — jamais en place. */
    const editTree = (path, fn) => {
        const clone = JSON.parse(JSON.stringify(tree));
        let node = clone;
        for (const key of path) {
            if (!node[key]) return;      // le chemin a disparu entre-temps
            node = node[key];
        }
        fn(node, clone);
        setClassification({ value: clone });
    };

    const addChild = (path) => {
        editTree(path, (node) => {
            // Un nom neuf et unique : « Nouvelle catégorie », « Nouvelle catégorie 2 »… Créer
            // deux fois le même nom écraserait la première — voir l'en-tête.
            const base = t("CLASSIFICATION_NEW_CATEGORY");
            let name = base, i = 2;
            while (Object.prototype.hasOwnProperty.call(node, name)) name = `${base} ${i++}`;
            node[name] = {};
        });
    };

    const addRoot = () => {
        const clone = JSON.parse(JSON.stringify(tree));
        const base = t("CLASSIFICATION_NEW_CATEGORY");
        let name = base, i = 2;
        while (Object.prototype.hasOwnProperty.call(clone, name)) name = `${base} ${i++}`;
        clone[name] = {};
        setClassification({ value: clone });
    };

    const deleteNode = (path) => {
        const parentPath = path.slice(0, -1);
        const leaf = path[path.length - 1];
        editTree(parentPath, (node) => { delete node[leaf]; });
    };

    /**
     * Renomme en PRÉSERVANT L'ORDRE des frères et sœurs.
     *
     * `delete` puis réinsertion enverrait la catégorie en fin de liste : l'ordre des clefs est
     * celui que voit l'agent, et le voir sauter à chaque correction de faute de frappe est
     * déroutant. On reconstruit donc l'objet à l'identique, en substituant la seule clef visée.
     */
    const renameNode = (path, next) => {
        if (!next) return t("CLASSIFICATION_NAME_REQUIRED");
        const parentPath = path.slice(0, -1);
        const current = path[path.length - 1];

        let parent = tree;
        for (const key of parentPath) parent = parent[key];
        if (Object.prototype.hasOwnProperty.call(parent, next)) {
            return t("CLASSIFICATION_DUPLICATE_NAME");
        }

        editTree(parentPath, (node) => {
            const rebuilt = {};
            Object.keys(node).forEach((k) => { rebuilt[k === current ? next : k] = node[k]; });
            Object.keys(node).forEach((k) => delete node[k]);
            Object.keys(rebuilt).forEach((k) => { node[k] = rebuilt[k]; });
        });
        return null;
    };

    const rootNames = Object.keys(tree);

    // Profondeur maximale, affichée à titre indicatif : c'est le nombre de niveaux que l'agent
    // devra parcourir sur la branche la plus longue.
    const depth = useMemo(() => {
        const walk = (node, d) => Object.keys(node || {}).reduce(
            (max, k) => Math.max(max, walk(node[k], d + 1)), d);
        return walk(tree, 0);
    }, [tree]);

    return (
        <div className="qcl-container">
            <div className="qcl-header">
                <div className="qcl-switch">
                    <Label weight="semibold">{t("CLASSIFICATION_ENABLED")}</Label>
                    <Switch
                        checked={classification.enabled === true}
                        disabled={readOnly}
                        onChange={(_, d) => setClassification({ enabled: d.checked })}
                    />
                </div>
                {rootNames.length > 0 && (
                    <Text size={200}>
                        {t("CLASSIFICATION_SUMMARY", { count: rootNames.length, depth })}
                    </Text>
                )}
            </div>

            <Text as="p" size={200} className="qcl-hint">{t("CLASSIFICATION_HINT")}</Text>

            {/* Activé sans arbre : l'onglet ne s'ouvrira jamais chez l'agent, et rien ne le dirait.
                C'est la seule incohérence qui mérite un avertissement ici. */}
            {classification.enabled === true && rootNames.length === 0 && (
                <div className="qcl-warning">{t("CLASSIFICATION_ENABLED_BUT_EMPTY")}</div>
            )}

            <div className="qcl-tree">
                {rootNames.length === 0 ? (
                    <div className="qcl-empty">{t("CLASSIFICATION_NO_CATEGORY")}</div>
                ) : rootNames.map((name) => (
                    <TreeNode
                        key={name}
                        path={[name]}
                        name={name}
                        node={tree[name]}
                        onRename={renameNode}
                        onDelete={deleteNode}
                        onAddChild={addChild}
                        readOnly={readOnly}
                        t={t}
                    />
                ))}
            </div>

            {!readOnly && (
                <Button appearance="primary" icon={<Add20Filled />} onClick={addRoot}>
                    {t("CLASSIFICATION_ADD_CATEGORY")}
                </Button>
            )}
        </div>
    );
};

export default QueueClassification;
